import styled from '@emotion/styled'
import { PlusOutlined } from '@ant-design/icons'
import {
  Button,
  Form,
  Input,
  InputNumber,
  Segmented,
  Switch,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd'
import type { UploadFile } from 'antd/es/upload/interface'
import { FC, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import StockImageThumb from 'components/ManageProduct/StockImageThumb'
import { allowFileExtensionsImage, backendUrl } from 'config'
import { useUpdateGeneralStock } from 'hooks/useGeneralStock'
import { useAdjustSkuStock, useSkuStockTransactions } from 'hooks/useProduct'
import { uploadImage } from 'services/factoryAPI'
import { numberFormatInt } from 'utils/nuberFormat'
import { createInputNumberGuards } from 'utils'
import type { GeneralStockFormEdit } from 'services/interface/generalStock.interface'
import type { SkuLinkedStockDraftRow } from 'services/interface/productLinkedStock.interface'
import { useSkuModalStockEdit } from './SkuModalStockEditContext'

type GeneralStockFormValues = {
  name?: string
  description?: string
  remark?: string
  is_active?: boolean
}

/** รูปของ linked row: ใช้ ref_image_id ประกอบ URL เองด้วย backendUrl ก่อน แล้ว fallback URL เต็มจาก backend */
const linkedRowThumbSrc = (row: {
  ref_image_id?: string | null
  ref_image_url?: string | null
}): string | undefined =>
  (row.ref_image_id ? `${backendUrl}images/${row.ref_image_id}` : undefined) ??
  row.ref_image_url ??
  undefined

const extractImageIds = (fileList: UploadFile[]): string[] =>
  fileList
    .map((f) => (f.response?.id as string) ?? (f as UploadFile & { id?: string }).id)
    .filter((id): id is string => !!id)

const buildFileListFromIds = (
  ids?: string[] | null,
  urls?: string[] | null
): UploadFile[] =>
  (ids ?? []).map((id, index) => ({
    uid: id,
    name: `image-${index + 1}`,
    status: 'done',
    id,
    url: urls?.[index],
  }))

const draftRowToGeneralEdit = (row: SkuLinkedStockDraftRow): GeneralStockFormEdit => ({
  id: row.ref_id,
  name: row.ref_label,
  description: row.ref_description ?? undefined,
  images: row.ref_image_id ? [row.ref_image_id] : undefined,
  image_urls: (() => {
    const src = linkedRowThumbSrc(row)
    return src ? [src] : undefined
  })(),
  quantity: row.ref_balance ?? 0,
  remark: undefined,
  safe_stock_quantity: 0,
  is_safe_stock_enabled: false,
  is_active: true,
})

type SkuLinkedStockEditPanelProps = {
  target: SkuLinkedStockDraftRow
  onClose: () => void
}

const SkuLinkedStockEditPanel: FC<SkuLinkedStockEditPanelProps> = ({ target, onClose }) => {
  const { productId, notifyStockSaved } = useSkuModalStockEdit()
  const queryClient = useQueryClient()

  const afterSaved = () => {
    queryClient.invalidateQueries({ queryKey: ['productLinkedStock'] })
    // คีย์จริงของ useSkuStockPicker/useCombinedStockPicker คือ 'stockPicker'
    // (เดิมใช้ 'productStockPicker' ซึ่งไม่ตรง → ตัวเลขสรุปสต็อกไม่อัปเดตหลังบันทึก)
    queryClient.invalidateQueries({ queryKey: ['stockPicker'] })
    notifyStockSaved()
    onClose()
  }

  if (target.ref_type === 'product_recipe' || target.source_kind === 'bundle') {
    return (
      <PanelRoot>
        <PanelHeader>
          <PanelTitle>แก้ไขสต็อก</PanelTitle>
          <Button type="link" size="small" style={{ padding: 0, marginLeft: 'auto' }} onClick={onClose}>
          ปิด
        </Button>
        </PanelHeader>
        <Typography.Text type="secondary">
          ชุดสินค้า/สูตรไม่สามารถแก้ไขสต็อกจากหน้านี้ได้
        </Typography.Text>
      </PanelRoot>
    )
  }

  if (target.source_kind === 'master') {
    return (
      <MasterStockEditForm
        target={target}
        onClose={onClose}
        onSaved={afterSaved}
      />
    )
  }

  return (
    <SkuStockAdjustForm
      target={target}
      productId={productId}
      onClose={onClose}
      onSaved={afterSaved}
    />
  )
}

export default SkuLinkedStockEditPanel

type MasterStockEditFormProps = {
  target: SkuLinkedStockDraftRow
  onClose: () => void
  onSaved: () => void
}

const MasterStockEditForm: FC<MasterStockEditFormProps> = ({
  target,
  onClose,
  onSaved,
}) => {
  const [form] = Form.useForm<GeneralStockFormValues>()
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const editing = draftRowToGeneralEdit(target)

  const { mutate: updateMutate, isPending: updating } = useUpdateGeneralStock()

  useEffect(() => {
    form.setFieldsValue({
      name: editing.name ?? undefined,
      description: editing.description ?? undefined,
      remark: editing.remark ?? undefined,
      is_active: editing.is_active !== false,
    })
    setFileList(buildFileListFromIds(editing.images, editing.image_urls))
  }, [target.key, editing, form])

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const images = extractImageIds(fileList)
      updateMutate(
        {
          id: editing.id,
          name: values.name?.trim() || undefined,
          description: values.description?.trim() || undefined,
          images: images.length > 0 ? images : undefined,
          remark: values.remark?.trim() || undefined,
          is_active: values.is_active !== false,
        },
        {
          onSuccess: () => {
            message.success('บันทึกสต็อกทั่วไปแล้ว')
            onSaved()
          },
          onError: (err) => message.error(err.message || 'บันทึกไม่สำเร็จ'),
        }
      )
    })
  }

  return (
    <PanelRoot>
      <PanelHeader>
        <PanelTitleRow>
          <ThumbWrap>
            <StockImageThumb src={linkedRowThumbSrc(target)} alt={target.ref_label} />
          </ThumbWrap>
          <div>
            <PanelTitle>แก้ไขข้อมูลทั่วไป</PanelTitle>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {target.ref_label}
            </Typography.Text>
          </div>
        </PanelTitleRow>
        <Button type="link" size="small" style={{ padding: 0, marginLeft: 'auto' }} onClick={onClose}>
          ปิด
        </Button>
      </PanelHeader>

      <PanelBody>
        <Form form={form} layout="vertical" requiredMark={false} size="small">
          <Form.Item
            label="ชื่อรายการ"
            name="name"
            rules={[{ required: true, message: 'กรุณากรอกชื่อรายการ' }]}
          >
            <Input placeholder="ชื่อรายการ" maxLength={255} />
          </Form.Item>
          <Form.Item label="รายละเอียด" name="description">
            <Input.TextArea rows={3} placeholder="รายละเอียด (ถ้ามี)" />
          </Form.Item>
          <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
            คงเหลือปัจจุบัน: <strong>{numberFormatInt(editing.quantity ?? 0)}</strong> ชิ้น — ปรับยอดจากแท็บปรับสต็อก / ประวัติ
          </Typography.Text>
          <Form.Item label="หมายเหตุ" name="remark">
            <Input placeholder="หมายเหตุ (ถ้ามี)" />
          </Form.Item>
          <Form.Item label="รูปภาพ (สูงสุด 5 รูป)">
            <Upload
              listType="picture-card"
              fileList={fileList}
              accept={allowFileExtensionsImage}
              maxCount={5}
              customRequest={async ({ file, onSuccess, onError }) => {
                try {
                  const result = await uploadImage(file as File)
                  onSuccess?.(result)
                } catch (e) {
                  onError?.(e as Error)
                }
              }}
              onChange={({ fileList: next }) => {
                const mapped = next.map((item) => {
                  if (item.response) {
                    return {
                      ...item,
                      id: item.response.id,
                      url: item.response.url,
                    }
                  }
                  return item
                })
                setFileList(mapped)
              }}
            >
              {fileList.length >= 5 ? null : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <PlusOutlined />
                  <span style={{ fontSize: 11 }}>อัปโหลด</span>
                </div>
              )}
            </Upload>
          </Form.Item>
          <Form.Item label="เปิดใช้งาน" name="is_active" valuePropName="checked">
            <Switch size="small" />
          </Form.Item>
        </Form>
      </PanelBody>

      <PanelFooter>
        <Button onClick={onClose}>ยกเลิก</Button>
        <Button type="primary" loading={updating} onClick={handleSubmit}>
          บันทึก
        </Button>
      </PanelFooter>
    </PanelRoot>
  )
}

type SkuStockAdjustFormProps = {
  target: SkuLinkedStockDraftRow
  productId?: string | null
  onClose: () => void
  onSaved: () => void
}

const SkuStockAdjustForm: FC<SkuStockAdjustFormProps> = ({
  target,
  productId,
  onClose,
  onSaved,
}) => {
  const [form] = Form.useForm<{ direction: 'in' | 'out'; quantity: number; remark?: string }>()
  // จำนวนสต็อกรับได้เฉพาะจำนวนเต็ม
  const integerGuards = createInputNumberGuards({ mode: 'integer' })
  const { mutateAsync: adjustApi, isPending: isAdjusting } = useAdjustSkuStock()
  const queryClient = useQueryClient()

  const [section, setSection] = useState<'adjust' | 'history'>('adjust')
  const [historyPage, setHistoryPage] = useState(1)
  const historyLimit = 10

  const skuId = target.ref_stock_sku_id

  const listTxInput = useMemo(
    () => ({
      product_id: productId ?? '',
      sku_id: skuId ?? '',
      page: historyPage,
      limit: historyLimit,
    }),
    [productId, skuId, historyPage]
  )

  const { data: txRes, isFetching: txLoading } = useSkuStockTransactions(
    listTxInput,
    section === 'history' && !!productId && !!skuId
  )

  const txRows = txRes?.data?.rows ?? []
  const txTotal = txRes?.data?.total ?? 0

  const historyColumns = useMemo(
    () => [
      {
        title: 'วันที่',
        dataIndex: 'create_date',
        key: 'create_date',
        width: 140,
        render: (d: string) => (d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '-'),
      },
      {
        title: 'ทิศทาง',
        dataIndex: 'direction',
        key: 'direction',
        width: 70,
        render: (d: string) =>
          d === 'in' ? <Tag color="blue">เข้า</Tag> : <Tag color="volcano">ออก</Tag>,
      },
      {
        title: 'จำนวน',
        dataIndex: 'quantity',
        key: 'quantity',
        width: 70,
        render: (q: number) => numberFormatInt(q ?? 0),
      },
      {
        title: 'หมายเหตุ',
        dataIndex: 'remark',
        key: 'remark',
        ellipsis: true,
      },
    ],
    []
  )

  useEffect(() => {
    form.setFieldsValue({ direction: 'in', quantity: 1, remark: '' })
    setSection('adjust')
    setHistoryPage(1)
  }, [target.key, form])

  const handleSubmit = async () => {
    if (!skuId) {
      message.warning('ไม่พบ SKU สำหรับปรับสต็อก')
      return
    }
    try {
      const v = await form.validateFields()
      // ส่งแค่ sku_id — backend resolve product_id เอง
      await adjustApi({
        sku_id: skuId,
        direction: v.direction,
        quantity: v.quantity,
        remark: v.remark,
      })
      message.success(v.direction === 'in' ? 'เพิ่มสต็อกเรียบร้อย' : 'ลดสต็อกเรียบร้อย')
      // refetch ประวัติเพื่อให้แท็บ "ประวัติ" เห็นรายการล่าสุด
      queryClient.invalidateQueries({ queryKey: ['skuStockTransactions'] })
      onSaved()
    } catch (err: unknown) {
      const e = err as { errorFields?: unknown; message?: string }
      if (e?.errorFields) return
      message.error(e?.message || 'ไม่สามารถปรับสต็อกได้')
    }
  }

  return (
    <PanelRoot>
      <PanelHeader>
        <PanelTitleRow>
          <ThumbWrap>
            <StockImageThumb src={linkedRowThumbSrc(target)} alt={target.ref_label} />
          </ThumbWrap>
          <div>
            <PanelTitle>ปรับสต็อก SKU</PanelTitle>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {target.ref_label}
            </Typography.Text>
          </div>
        </PanelTitleRow>
        <Button type="link" size="small" style={{ padding: 0, marginLeft: 'auto' }} onClick={onClose}>
          ปิด
        </Button>
      </PanelHeader>

      <PanelBody>
        <SectionSwitch>
          <Segmented
            block
            size="small"
            value={section}
            onChange={(v) => setSection(v as 'adjust' | 'history')}
            options={[
              { label: 'ปรับสต็อก', value: 'adjust' },
              { label: 'ประวัติ', value: 'history' },
            ]}
          />
        </SectionSwitch>

        {section === 'adjust' ? (
          <Form disabled={isAdjusting} form={form} layout="vertical" size="small">
            <Form.Item label="การทำงาน" name="direction" rules={[{ required: true }]}>
              <Segmented
                block
                options={[
                  { label: 'เพิ่มเข้าคลัง', value: 'in' },
                  { label: 'นำออก', value: 'out' },
                ]}
              />
            </Form.Item>
            <Form.Item
              label="จำนวน"
              name="quantity"
              rules={[
                { required: true, message: 'กรุณาระบุจำนวน' },
                {
                  validator: (_, v) =>
                    v >= 1 ? Promise.resolve() : Promise.reject(new Error('จำนวนต้องไม่น้อยกว่า 1')),
                },
              ]}
            >
              <InputNumber
                min={1}
                precision={0}
                step={1}
                style={{ width: '100%' }}
                {...integerGuards}
              />
            </Form.Item>
            <Form.Item label="หมายเหตุ (ถ้ามี)" name="remark">
              <Input.TextArea rows={2} placeholder="เช่น รับของ / ของเสียหาย" />
            </Form.Item>
          </Form>
        ) : (
          <HistoryWrap>
            <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
              รายการเคลื่อนไหวล่าสุดของ SKU นี้
            </Typography.Paragraph>
            <Table
              rowKey="id"
              size="small"
              loading={txLoading}
              columns={historyColumns}
              dataSource={txRows}
              scroll={{ x: 'max-content' }}
              pagination={{
                current: historyPage,
                pageSize: historyLimit,
                total: txTotal,
                showSizeChanger: false,
                size: 'small',
                onChange: (p) => setHistoryPage(p),
              }}
            />
          </HistoryWrap>
        )}
      </PanelBody>

      {section === 'adjust' ? (
        <PanelFooter>
          <Button onClick={onClose} disabled={isAdjusting}>
            ยกเลิก
          </Button>
          <Button type="primary" loading={isAdjusting} onClick={handleSubmit}>
            บันทึก
          </Button>
        </PanelFooter>
      ) : null}
    </PanelRoot>
  )
}

const SectionSwitch = styled.div`
  margin-bottom: 12px;
`

const HistoryWrap = styled.div`
  .ant-table {
    font-size: 12px;
  }
  .ant-table-thead > tr > th {
    font-size: 11px;
    padding: 6px 8px;
  }
  .ant-table-tbody > tr > td {
    font-size: 12px;
    padding: 6px 8px;
  }
`

const PanelRoot = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const PanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  padding: 14px 14px 10px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
`

const PanelTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`

const ThumbWrap = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 6px;
  flex-shrink: 0;
  border: 1px solid #f0f0f0;
  overflow: hidden;
`

const PanelTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #262626;
  line-height: 1.3;
`

const PanelBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px 14px;
`

const PanelFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 14px 14px;
  border-top: 1px solid #f0f0f0;
  flex-shrink: 0;
`
